import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AlertTriangle, Sparkles, Info, Download, FileDown, Flag, XCircle, ExternalLink, PanelRightOpen, ListTree, MessageSquare, X } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
import AppShell from "@/components/AppShell";
import DateField from "@/components/DateField";
import DocUpload from "@/components/DocUpload";
import CaseLeftNav from "@/components/CaseLeftNav";
import { CaseReadinessCard } from "@/components/CaseReadinessCard";
import VisaTrackingCard from "@/components/VisaTrackingCard";
import AIPanel from "@/components/AIPanel";
import NoticesCard from "@/components/NoticesCard";
import TimelineCard from "@/components/TimelineCard";
import ShareWithAttorneyCard from "@/components/ShareWithAttorneyCard";
import AssignAttorneyCard from "@/components/AssignAttorneyCard";
import BeneficiaryInvitesCard from "@/components/BeneficiaryInvitesCard";
import RosterStage from "@/components/RosterStage";
import { GuidedTour, CASE_WORKSPACE_TOUR_STEPS } from "@/lib/tour";

const FIELDS_BY_STAGE = {
  roster: [],
  petitioner_info: [
    { id: "petitioner_first_name", label: "First name" },
    { id: "petitioner_last_name", label: "Last name" },
    { id: "petitioner_dob", label: "Date of birth", type: "date" },
    { id: "petitioner_place_of_birth", label: "Place of birth (city, country)" },
    {
      id: "petitioner_citizenship_status",
      label: "U.S. status",
      type: "select",
      options: ["U.S. Citizen", "Lawful Permanent Resident"],
    },
    { id: "petitioner_a_number_or_ssn", label: "A-Number or SSN" },
    { id: "petitioner_address", label: "U.S. address" },
    { id: "petitioner_phone", label: "Phone" },
    { id: "petitioner_email", label: "Email", type: "email" },
  ],
  beneficiary_info: [
    { id: "beneficiary_first_name", label: "First name" },
    { id: "beneficiary_last_name", label: "Last name" },
    { id: "beneficiary_dob", label: "Date of birth", type: "date" },
    { id: "beneficiary_place_of_birth", label: "Place of birth (city, country)" },
    { id: "beneficiary_nationality", label: "Nationality" },
    {
      id: "beneficiary_marital_status",
      label: "Marital status",
      type: "select",
      options: ["Single", "Married", "Divorced", "Widowed"],
    },
    { id: "beneficiary_address", label: "Current address" },
    {
      id: "beneficiary_currently_in_us",
      label: "Is the beneficiary currently in the United States?",
      type: "select",
      options: [
        { value: "yes", label: "Yes — they are in the U.S." },
        { value: "no", label: "No — they are abroad (consular processing)" },
      ],
      help: "Choose 'No' if the beneficiary will interview at a U.S. consulate abroad. This will unlock the DS-260 stage. If they are already in the U.S. and will Adjust Status (I-485), this platform isn't the right fit — GreenCardsUSA.ai handles that path.",
    },
  ],
  marriage_history: [
    { id: "marriage_date", label: "Marriage date", type: "date" },
    { id: "marriage_place", label: "Marriage place (city, country)" },
    {
      id: "spouse_previously_married",
      label: "Was the beneficiary previously married?",
      type: "select",
      options: ["No", "Yes"],
    },
    {
      id: "prior_marriage_end_date",
      label: "Prior marriage end date (if any)",
      type: "date",
      optional: true,
    },
  ],
  children: [],
  evidence: [],
  review: [],
  address_history: [],
  employment_history: [],
  family_history: [],
  ds260: [
    // Personal
    { id: "ds260_sex", label: "Sex", type: "select", options: ["male", "female"] },
    { id: "ds260_full_name_native_alphabet", label: "Full name in native alphabet", help: "If your name is written in a non-Latin script (e.g. Chinese, Arabic, Russian), enter it here. Leave blank if it's already in Latin letters." },
    { id: "ds260_other_names_used", label: "Other names ever used (aliases, maiden name)", help: "Comma-separated." },
    // Contact
    { id: "ds260_present_address_abroad", label: "Present address abroad", help: "Full mailing address where the beneficiary currently lives." },
    { id: "ds260_present_country", label: "Country of residence" },
    { id: "ds260_primary_phone", label: "Primary phone (with country code)" },
    { id: "ds260_email", label: "Email", type: "email" },
    { id: "ds260_confirmation_number", label: "DS-260 confirmation number (after you submit at ceac.state.gov)", optional: true, help: "Shown on your CEAC confirmation page after submission, e.g. AA00XXXXXX. Recording it here completes your workflow." },
    // Passport
    { id: "ds260_passport_number", label: "Passport number" },
    { id: "ds260_passport_country_of_issuance", label: "Country that issued the passport" },
    { id: "ds260_passport_issue_date", label: "Passport issue date", type: "date" },
    { id: "ds260_passport_expiration_date", label: "Passport expiration date", type: "date" },
    // Nationality
    { id: "ds260_country_of_nationality", label: "Country of nationality" },
    { id: "ds260_other_nationalities", label: "Other nationalities (if any)", help: "Comma-separated. Leave blank if you only hold one." },
    { id: "ds260_national_id_number", label: "National ID number (if your country issues one)" },
    // US Intent
    { id: "ds260_intended_us_address", label: "Address in the U.S. where you will live" },
    { id: "ds260_us_port_of_entry", label: "Intended U.S. port of entry", help: "e.g. SFO, JFK, LAX." },
    // Security disclosures — required by USCIS
    { id: "ds260_has_communicable_disease", label: "Do you have a communicable disease of public health significance?", type: "select", options: ["no", "yes"] },
    { id: "ds260_is_drug_abuser", label: "Are you a drug abuser or addict?", type: "select", options: ["no", "yes"] },
    { id: "ds260_ever_arrested_or_convicted", label: "Have you ever been arrested or convicted of any crime?", type: "select", options: ["no", "yes"] },
    { id: "ds260_ever_deported_or_removed", label: "Have you ever been deported or removed from the U.S.?", type: "select", options: ["no", "yes"] },
    { id: "ds260_ever_engaged_in_terrorism", label: "Have you ever engaged in terrorism?", type: "select", options: ["no", "yes"] },
    { id: "ds260_security_narrative", label: "If you answered 'yes' to any security question above, please explain here", type: "textarea", optional: true },
  ],
};

const STAGE_SUBTITLES = {
  roster:
    "Upload documents person-by-person — each one is read and organized into the right case fields for you to review.",
  petitioner_info: "Tell us about the U.S. citizen or LPR filing the petition.",
  beneficiary_info: "Now, details about the family member being petitioned for.",
  address_history:
    "Every address the beneficiary has lived at during the last 5 years — the DS-260 asks for full address history.",
  employment_history:
    "Every employer (or period of unemployment) during the last 5 years — the DS-260 asks for full work history.",
  family_history:
    "Names, dates, and birthplaces of the petitioner's and beneficiary's parents.",
  marriage_history:
    "Only for spouse petitions — evidence of a bona fide marriage.",
  children:
    "List any children (biological, adopted, or step-children) of the beneficiary.",
  evidence:
    "Anything else — bank statements, wedding photos, tax returns. Not tied to a specific person.",
  review: "Review everything before generating the form packet.",
  ds260:
    "DS-260 · Consular Processing. Only shown because the beneficiary is currently outside the U.S. These fields are required for the consular interview.",
};

function MobileToolbar({ caseData, stage, onOpenStages, onOpenAI }) {
  return (
    <div
      className="md:hidden sticky top-0 z-20 flex items-center justify-between gap-2 px-3 py-2 border-b backdrop-blur-md"
      style={{ borderColor: "var(--border-stone)", background: "rgba(249,248,245,0.9)" }}
      data-testid="case-mobile-toolbar"
    >
      <button
        type="button"
        onClick={onOpenStages}
        className="btn-ghost text-xs flex items-center gap-1.5 flex-1"
        style={{ border: "1px solid var(--border-stone)", background: "#fff", padding: "0.5rem 0.75rem" }}
        data-testid="mobile-open-stages-btn"
      >
        <ListTree className="w-4 h-4" style={{ color: "var(--action)" }} />
        <span className="truncate">
          {caseData.completion_percent}% · {caseData.stages.find((s) => s.id === stage)?.label || "Stages"}
        </span>
      </button>
      <button
        type="button"
        onClick={onOpenAI}
        className="btn-ghost text-xs flex items-center gap-1.5"
        style={{
          border: "1px solid var(--ai-border)",
          background: "var(--ai-bg)",
          color: "var(--ai-text)",
          padding: "0.5rem 0.75rem",
        }}
        data-testid="mobile-open-ai-btn"
        aria-label="Open your case companion chat"
      >
        <MessageSquare className="w-4 h-4" />
        <span>AI</span>
      </button>
    </div>
  );
}

function MobileStagesSheet({ caseData, stage, stagesDone, onStageChange, onClose }) {
  return (
    <div className="mobile-sheet md:hidden" onClick={onClose} data-testid="mobile-stages-sheet">
      <div className="mobile-sheet-inner" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-grabber" />
        <div className="flex items-center justify-between px-2 pb-2">
          <div className="font-display font-bold text-lg">Case stages</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="mobile-sheet-close-btn"
            className="p-1.5 rounded hover:bg-black/5"
          >
            <X className="w-5 h-5" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>
        <CaseLeftNav
          caseData={caseData}
          stage={stage}
          stagesDone={stagesDone}
          variant="sheet"
          onStageChange={onStageChange}
        />
      </div>
    </div>
  );
}

function MobileAISheet({ onClose, aiPanelProps }) {
  return (
    <div className="mobile-sheet md:hidden" onClick={onClose} data-testid="mobile-ai-sheet">
      <div className="mobile-sheet-inner p-0" onClick={(e) => e.stopPropagation()} style={{ maxHeight: "90vh" }}>
        <div className="sheet-grabber mt-3" />
        <div className="flex items-center justify-between px-4 pb-2">
          <div className="font-display font-bold text-lg" style={{ color: "var(--ai-text)" }}>
            Case Companion
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close AI panel"
            className="p-1.5 rounded hover:bg-black/5"
            data-testid="mobile-ai-close-btn"
          >
            <X className="w-5 h-5" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>
        <div style={{ height: "calc(90vh - 60px)" }}>
          <AIPanel {...aiPanelProps} variant="sheet" />
        </div>
      </div>
    </div>
  );
}

export default function CaseWorkspace() {
  const { id } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [aiHint, setAiHint] = useState(null);
  const [flagsByField, setFlagsByField] = useState({});
  // Session activity — reinforces the paralegal metaphor
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [fieldsSinceChat, setFieldsSinceChat] = useState(0);
  // Mobile: which side sheet is open ("stages" | "ai" | null)
  const [mobileSheet, setMobileSheet] = useState(null);

  const loadCase = useCallback(async () => {
    const { data } = await api.get(`/cases/${id}`);
    setCaseData(data);
  }, [id]);

  const loadDocs = useCallback(async () => {
    const { data } = await api.get(`/documents`, { params: { case_id: id } });
    setDocuments(data);
  }, [id]);

  const loadChat = useCallback(async () => {
    const { data } = await api.get(`/ai/chat/history`, { params: { case_id: id } });
    setChatMsgs(data);
  }, [id]);

  const loadFlags = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${id}/flags`);
      const byField = {};
      for (const f of data.flags) byField[f.field] = f;
      setFlagsByField(byField);
    } catch (err) {
      // 404 is normal for cases that don't have flag data yet — silently
      // ignore. Only surface real errors.
      if (err?.response?.status !== 404) {
        console.warn("flags load failed:", err?.message || err);
      }
    }
  }, [id]);

  useEffect(() => {
    loadCase();
    loadDocs();
    loadChat();
    loadFlags();
  }, [loadCase, loadDocs, loadChat, loadFlags]);

  const setStage = useCallback(
    async (sid) => {
      const { data } = await api.patch(`/cases/${id}`, { stage: sid });
      setCaseData(data);
    },
    [id],
  );

  // Smart Sort dispatches this event when the user clicks "Review your DS-260"
  useEffect(() => {
    const jump = (e) => setStage(e.detail || "review");
    window.addEventListener("gcu:goto-stage", jump);
    return () => window.removeEventListener("gcu:goto-stage", jump);
  }, [setStage]);

  const saveField = useCallback(
    async (field, value) => {
      setSavingKey(field);
      try {
        const { data } = await api.patch(`/cases/${id}`, {
          section_data: { [field]: value },
        });
        setCaseData(data);
        setLastSavedAt(Date.now());
        setFieldsSinceChat((n) => n + 1);
        loadFlags();
      } finally {
        setSavingKey(null);
      }
    },
    [id, loadFlags],
  );

  const resolveFlag = useCallback(
    async (field, action) => {
      try {
        await api.post(`/cases/${id}/fields/${field}/resolve`, { action });
        await loadCase();
        await loadFlags();
      } catch (err) {
        console.error("resolve failed:", err);
      }
    },
    [id, loadCase, loadFlags],
  );

  const toggleReview = useCallback(
    async (field, on, note = null) => {
      try {
        await api.post(`/cases/${id}/fields/${field}/review`, { review_flag: on, review_note: note });
        await loadFlags();
      } catch (err) {
        console.error("review toggle failed:", err);
      }
    },
    [id, loadFlags],
  );

  const sendChat = useCallback(async () => {
    const msg = chatInput.trim();
    if (!msg) return;
    setChatInput("");
    const now = new Date().toISOString();
    setChatMsgs((m) => [...m, { role: "user", content: msg, ts: now }]);
    setChatBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { case_id: id, message: msg });
      setChatMsgs((m) => [
        ...m,
        { role: "assistant", content: data.reply, ts: new Date().toISOString() },
      ]);
      // Only reset on successful chat, so a failed send doesn't hide the user's progress badge.
      setFieldsSinceChat(0);
    } catch (err) {
      console.error("AI chat failed:", err?.message || err);
      setChatMsgs((m) => [
        ...m,
        {
          role: "assistant",
          content: "Sorry, I hit an error reaching the AI. Please try again.",
          ts: new Date().toISOString(),
        },
      ]);
    } finally {
      setChatBusy(false);
    }
  }, [chatInput, id]);

  const explainField = useCallback(
    async (fieldId, currentStage) => {
      setAiHint({ field_id: fieldId, explanation: "…" });
      try {
        const { data } = await api.post("/ai/field-explain", {
          case_type: "DS-260",
          stage: currentStage,
          field_id: fieldId,
          language: "auto",
        });
        setAiHint(data);
      } catch (err) {
        console.error("field-explain failed:", err?.message || err);
        setAiHint({ field_id: fieldId, explanation: "Could not load explanation." });
      }
    },
    [],
  );

  if (!caseData) {
    return (
      <AppShell>
        <div className="p-10 text-sm" style={{ color: "var(--text-secondary)" }}>
          Loading case…
        </div>
      </AppShell>
    );
  }

  const stage = caseData.stage;
  // Hide DS-260 stage unless the beneficiary is abroad (consular processing).
  const ds260Applicable = (caseData.data || {}).beneficiary_currently_in_us !== "yes";
  const visibleStages = (caseData.stages || []).filter(
    (s) => s.id !== "ds260" || ds260Applicable,
  );
  const filteredCaseData = { ...caseData, stages: visibleStages };
  const stagesDone = new Set(
    visibleStages
      .filter((s) =>
        (FIELDS_BY_STAGE[s.id] || []).every(
          (f) => f.optional || (caseData.data || {})[f.id],
        ),
      )
      .map((s) => s.id),
  );

  const currentFields = FIELDS_BY_STAGE[stage] || [];
  const aiPanelProps = {
    caseData, chatMsgs, chatBusy, chatInput,
    onChangeInput: setChatInput, onSend: sendChat,
    lastSavedAt, fieldsSinceChat,
  };

  return (
    <AppShell>
      {stage === "roster" && <GuidedTour tourId="case-workspace-v1" steps={CASE_WORKSPACE_TOUR_STEPS} />}
      <div className="h-[calc(100vh-52px)] sm:h-[calc(100vh-64px)] flex overflow-hidden" data-testid="case-workspace">
        {/* Desktop left nav */}
        <div className="hidden md:block">
          <CaseLeftNav
            caseData={filteredCaseData}
            stage={stage}
            stagesDone={stagesDone}
            onStageChange={setStage}
          />
        </div>

        <main className="flex-1 overflow-y-auto">
          <MobileToolbar
            caseData={caseData}
            stage={stage}
            onOpenStages={() => setMobileSheet("stages")}
            onOpenAI={() => setMobileSheet("ai")}
          />

          <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6 sm:py-10 anim-in" key={stage}>
            <div className="mb-6 sm:mb-8">
              <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--text-secondary)" }}>
                Stage
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold" style={{ color: "var(--text-primary)" }}>
                {caseData.stages.find((s) => s.id === stage)?.label}
              </h1>
              <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                {STAGE_SUBTITLES[stage]}
              </p>
            </div>

            <StageBody
              stage={stage}
              caseId={id}
              caseData={caseData}
              currentFields={currentFields}
              documents={documents}
              savingKey={savingKey}
              aiHint={aiHint}
              flagsByField={flagsByField}
              onSaveField={saveField}
              onExplain={(fieldId) => explainField(fieldId, stage)}
              onCloseHint={() => setAiHint(null)}
              onResolveFlag={resolveFlag}
              onToggleReview={toggleReview}
              onDocChange={async () => {
                await loadCase();
                await loadDocs();
                await loadFlags();
              }}
            />
          </div>
        </main>

        {/* Desktop AI panel */}
        <div className="hidden md:flex">
          <AIPanel {...aiPanelProps} />
        </div>
      </div>

      {/* Mobile bottom sheets */}
      {mobileSheet === "stages" && (
        <MobileStagesSheet
          caseData={caseData}
          stage={stage}
          stagesDone={stagesDone}
          onStageChange={(sid) => { setStage(sid); setMobileSheet(null); }}
          onClose={() => setMobileSheet(null)}
        />
      )}

      {mobileSheet === "ai" && (
        <MobileAISheet onClose={() => setMobileSheet(null)} aiPanelProps={aiPanelProps} />
      )}
    </AppShell>
  );
}

/* ---------- StageBody dispatch ---------- */

function StageBody({
  stage,
  caseId,
  caseData,
  currentFields,
  documents,
  savingKey,
  aiHint,
  flagsByField,
  onSaveField,
  onExplain,
  onCloseHint,
  onResolveFlag,
  onToggleReview,
  onDocChange,
}) {
  if (stage === "roster") {
    return (
      <RosterStage
        caseId={caseId}
        caseData={caseData}
        onCaseChange={onDocChange}
      />
    );
  }
  if (stage === "evidence") {
    return (
      <DocUpload
        caseId={caseId}
        requiredDocs={caseData.required_documents}
        documents={documents}
        onChange={onDocChange}
      />
    );
  }
  if (stage === "review")
    return (
      <div className="space-y-6">
        <CaseReadinessCard caseId={caseId} />
        <VisaTrackingCard caseId={caseId} />
        <div className="flex justify-end">
          <a
            href={`${BACKEND_URL}/api/cases/${caseId}/snapshot.pdf`}
            className="btn-ghost text-sm inline-flex items-center gap-1.5"
            style={{ border: "1px solid var(--border-stone)", background: "#fff" }}
            data-testid="download-snapshot-pdf-btn"
          >
            <FileDown className="w-4 h-4" /> Download case snapshot (PDF)
          </a>
        </div>
        <ReviewSection caseData={caseData} documents={documents} />
      </div>
    );
  if (stage === "children")
    return <ChildrenSection caseData={caseData} onSave={onSaveField} />;
  if (stage === "address_history")
    return <AddressHistorySection caseData={caseData} onSave={onSaveField} />;
  if (stage === "employment_history")
    return <EmploymentHistorySection caseData={caseData} onSave={onSaveField} />;
  if (stage === "family_history")
    return <FamilyHistorySection caseData={caseData} onSave={onSaveField} />;
  return (
    <FormFields
      fields={currentFields}
      data={caseData.data || {}}
      savingKey={savingKey}
      onSave={onSaveField}
      onExplain={onExplain}
      aiHint={aiHint}
      onCloseHint={onCloseHint}
      flagsByField={flagsByField}
      onResolveFlag={onResolveFlag}
      onToggleReview={onToggleReview}
    />
  );
}

/* ---------- FormFields ---------- */

function FormFields({ fields, data, savingKey, onSave, onExplain, aiHint, onCloseHint,
                     flagsByField, onResolveFlag, onToggleReview }) {
  return (
    <div className="card-surface p-8 space-y-6" data-testid="form-card">
      {fields.length === 0 && (
        <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
          Nothing to fill in this stage.
        </div>
      )}
      {fields.map((f) => (
        <FieldRow
          key={f.id}
          f={f}
          value={data[f.id] ?? ""}
          savingKey={savingKey}
          onSave={onSave}
          onExplain={onExplain}
          aiHint={aiHint}
          onCloseHint={onCloseHint}
          flag={flagsByField?.[f.id]}
          onResolveFlag={onResolveFlag}
          onToggleReview={onToggleReview}
        />
      ))}
    </div>
  );
}

function FieldRow({ f, value, savingKey, onSave, onExplain, aiHint, onCloseHint,
                    flag, onResolveFlag, onToggleReview }) {
  const [local, setLocal] = useState(value);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const commit = useCallback(() => {
    if (local !== value) onSave(f.id, local);
  }, [local, value, onSave, f.id]);

  const hintOpen = aiHint?.field_id === f.id;

  return (
    <div data-testid={`field-row-${f.id}`}>
      <div className="flex items-center justify-between">
        <label className="field-label">
          {f.label}
          {f.optional && (
            <span
              className="text-[10px] font-normal"
              style={{ color: "var(--text-secondary)" }}
            >
              &nbsp;(optional)
            </span>
          )}
        </label>
        <div className="flex items-center gap-2">
          <button
            data-testid={`review-flag-${f.id}`}
            type="button"
            onClick={() => onToggleReview(f.id, flag?.status !== "purple")}
            title={flag?.status === "purple" ? "Clear review flag" : "Flag for review"}
            className="text-xs flex items-center gap-1 hover:underline"
            style={{ color: flag?.status === "purple" ? "#6f42c1" : "var(--text-secondary)" }}
          >
            <Flag className="w-3 h-3" /> {flag?.status === "purple" ? "Flagged" : "Flag"}
          </button>
          <button
            data-testid={`explain-${f.id}`}
            type="button"
            onClick={() => onExplain(f.id)}
            title="Ask AI to explain this field"
            className="text-xs flex items-center gap-1 hover:underline"
            style={{ color: "var(--ai-text)" }}
          >
            <Info className="w-3 h-3" /> Explain
          </button>
        </div>
      </div>

      <FieldInput
        f={f}
        local={local}
        setLocal={setLocal}
        commit={commit}
        onSave={onSave}
      />

      {savingKey === f.id && (
        <div className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
          Saving…
        </div>
      )}

      {hintOpen && (
        <div
          className="mt-2 p-3 rounded-md text-xs anim-in"
          style={{
            background: "var(--ai-bg)",
            border: "1px solid var(--ai-border)",
            color: "var(--text-primary)",
          }}
          data-testid={`hint-${f.id}`}
        >
          <div className="flex items-center justify-between mb-1">
            <div
              className="flex items-center gap-1.5 font-semibold"
              style={{ color: "var(--ai-text)" }}
            >
              <Sparkles className="w-3 h-3" /> Helpful hint
            </div>
            <button
              onClick={onCloseHint}
              className="text-xs"
              style={{ color: "var(--text-secondary)" }}
            >
              close
            </button>
          </div>
          <div className="whitespace-pre-wrap leading-relaxed">{aiHint.explanation}</div>
        </div>
      )}
    </div>
  );
}

function FieldInput({ f, local, setLocal, commit, onSave }) {
  if (f.type === "select") {
    return (
      <select
        className="field-input"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        data-testid={`input-${f.id}`}
      >
        <option value="">Select…</option>
        {f.options.map((o) => {
          const val = typeof o === "object" && o !== null ? o.value : o;
          const label = typeof o === "object" && o !== null ? o.label : o;
          return (
            <option key={val} value={val}>
              {label}
            </option>
          );
        })}
      </select>
    );
  }
  if (f.type === "date") {
    return (
      <DateField
        value={local}
        testId={`input-${f.id}`}
        onCommit={(iso) => {
          setLocal(iso);
          onSave(f.id, iso);
        }}
      />
    );
  }
  return (
    <input
      data-testid={`input-${f.id}`}
      className="field-input"
      type={f.type || "text"}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
    />
  );
}

function _flagStyle(status) {
  if (status === "red") return { color: "#dc3545", bg: "#FEF2F2" };
  if (status === "yellow") return { color: "#b45309", bg: "#FEF3C7" };
  return { color: "#6f42c1", bg: "#F5F3FF" };
}

function FlagPanel({ flag, onClose, onResolve }) {
  const isRed = flag.status === "red";
  const isYellow = flag.status === "yellow";
  const { color, bg } = _flagStyle(flag.status);
  return (
    <div
      className="mt-2 p-3 rounded-md text-xs anim-in"
      style={{ background: bg, border: `1px solid ${color}44`, color: "var(--text-primary)" }}
      data-testid={`flag-panel-${flag.field}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 font-semibold" style={{ color }}>
          <Flag className="w-3.5 h-3.5" />
          {isRed && "External change — review required"}
          {isYellow && "Form value differs from extracted document value"}
          {flag.status === "purple" && "Marked for review"}
        </div>
        <button onClick={onClose} style={{ color: "var(--text-secondary)" }}>
          <XCircle className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3 text-[11px]">
        {flag.previous_value !== undefined && (
          <div>
            <div className="uppercase tracking-wide font-semibold" style={{ color: "var(--text-secondary)" }}>Previous</div>
            <div className="mt-0.5" style={{ color: "var(--text-primary)" }}>{String(flag.previous_value ?? "—") || <i>empty</i>}</div>
          </div>
        )}
        <div>
          <div className="uppercase tracking-wide font-semibold" style={{ color: "var(--text-secondary)" }}>Current</div>
          <div className="mt-0.5" style={{ color: "var(--text-primary)" }}>{String(flag.current_value ?? "—") || <i>empty</i>}</div>
        </div>
        {flag.db_value !== undefined && flag.db_value !== null && (
          <div>
            <div className="uppercase tracking-wide font-semibold" style={{ color: "var(--text-secondary)" }}>
              {flag.db_source === "document" ? "From document" : "From DB"}
            </div>
            <div className="mt-0.5" style={{ color: "var(--text-primary)" }}>{String(flag.db_value)}</div>
          </div>
        )}
      </div>
      {flag.modified_by && (
        <div className="text-[10.5px] mb-2" style={{ color: "var(--text-secondary)" }}>
          Changed by <b>{flag.modified_by}</b>
          {flag.modified_at && ` on ${new Date(flag.modified_at).toLocaleString()}`}
        </div>
      )}
      {isRed && (
        <div className="flex gap-2">
          <button
            className="px-2.5 py-1 text-xs font-semibold rounded"
            data-testid={`flag-keep-${flag.field}`}
            style={{ background: "#065F46", color: "#fff" }}
            onClick={() => onResolve("keep")}
          >
            Keep change
          </button>
          <button
            className="px-2.5 py-1 text-xs font-semibold rounded"
            data-testid={`flag-restore-${flag.field}`}
            style={{ background: "#dc3545", color: "#fff" }}
            onClick={() => onResolve("restore_previous")}
          >
            Restore previous
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- Children ---------- */

function ChildrenSection({ caseData, onSave }) {
  const children = caseData.data?.children || [];

  const ensureIds = (arr) =>
    arr.map((c) => (c.uid ? c : { ...c, uid: crypto.randomUUID() }));

  const add = () =>
    onSave("children", [
      ...ensureIds(children),
      { uid: crypto.randomUUID(), first_name: "", last_name: "", dob: "" },
    ]);

  const update = (uid, key, val) => {
    const next = ensureIds(children).map((c) =>
      c.uid === uid ? { ...c, [key]: val } : c,
    );
    onSave("children", next);
  };

  const remove = (uid) =>
    onSave(
      "children",
      ensureIds(children).filter((c) => c.uid !== uid),
    );

  const list = ensureIds(children);

  return (
    <div className="space-y-4">
      {list.length === 0 && (
        <div
          className="card-surface p-6 text-sm"
          style={{ color: "var(--text-secondary)" }}
        >
          No children added. If the beneficiary has children, add them here.
        </div>
      )}
      {list.map((c) => (
        <div key={c.uid} className="card-surface p-6" data-testid={`child-row-${c.uid}`}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="field-label">First name</label>
              <input
                className="field-input"
                value={c.first_name}
                onChange={(e) => update(c.uid, "first_name", e.target.value)}
              />
            </div>
            <div>
              <label className="field-label">Last name</label>
              <input
                className="field-input"
                value={c.last_name}
                onChange={(e) => update(c.uid, "last_name", e.target.value)}
              />
            </div>
            <div className="col-span-2">
              <label className="field-label">Date of birth</label>
              <DateField
                value={c.dob}
                onCommit={(iso) => update(c.uid, "dob", iso)}
                testId={`child-dob-${c.uid}`}
              />
            </div>
          </div>
          <div className="mt-4 text-right">
            <button
              className="text-xs"
              style={{ color: "var(--action)" }}
              onClick={() => remove(c.uid)}
            >
              Remove child
            </button>
          </div>
        </div>
      ))}
      <button
        className="btn-ghost text-sm border rounded-lg py-2 px-4"
        onClick={add}
        data-testid="add-child-btn"
        style={{ borderColor: "var(--border-stone)" }}
      >
        + Add child
      </button>
    </div>
  );
}

/* ---------- Repeater helpers ---------- */

function useRepeater(caseData, key, blank) {
  const list = caseData.data?.[key] || [];
  const ensureIds = (arr) =>
    arr.map((r) => (r.uid ? r : { ...r, uid: crypto.randomUUID() }));
  return { list: ensureIds(list), ensureIds, blank };
}

function RepeaterCard({ title, empty, list, onAdd, onRemove, children, testId, addLabel }) {
  return (
    <div className="space-y-4" data-testid={testId}>
      {list.length === 0 && (
        <div
          className="card-surface p-6 text-sm"
          style={{ color: "var(--text-secondary)" }}
        >
          {empty}
        </div>
      )}
      {list.map((row, idx) => (
        <div key={row.uid} className="card-surface p-6" data-testid={`${testId}-row-${idx}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-sm" style={{ color: "var(--text-primary)" }}>
              {title} #{idx + 1}
            </div>
            <button
              className="text-xs"
              style={{ color: "var(--action)" }}
              onClick={() => onRemove(row.uid)}
              data-testid={`${testId}-remove-${idx}`}
            >
              Remove
            </button>
          </div>
          {children(row)}
        </div>
      ))}
      <button
        className="btn-ghost text-sm border rounded-lg py-2 px-4"
        onClick={onAdd}
        data-testid={`${testId}-add`}
        style={{ borderColor: "var(--border-stone)" }}
      >
        + {addLabel}
      </button>
    </div>
  );
}

/* ---------- Address History (last 5 years) ---------- */

function AddressHistorySection({ caseData, onSave }) {
  const { list, ensureIds } = useRepeater(caseData, "address_history", {});
  const key = "address_history";

  const add = () =>
    onSave(key, [
      ...list,
      { uid: crypto.randomUUID(), street: "", unit: "", city: "", state: "", postal_code: "", country: "", from: "", to: "" },
    ]);
  const update = (uid, k, v) =>
    onSave(key, list.map((r) => (r.uid === uid ? { ...r, [k]: v } : r)));
  const remove = (uid) => onSave(key, ensureIds(list).filter((r) => r.uid !== uid));

  return (
    <RepeaterCard
      title="Address"
      empty="No addresses added yet. Add every place the beneficiary has lived in the last 5 years."
      list={list}
      onAdd={add}
      onRemove={remove}
      testId="address-history"
      addLabel="Add address"
    >
      {(r) => (
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="field-label">Street</label>
            <input className="field-input" value={r.street || ""}
              onChange={(e) => update(r.uid, "street", e.target.value)}
              data-testid={`addr-street-${r.uid}`} />
          </div>
          <div>
            <label className="field-label">Apt / Unit</label>
            <input className="field-input" value={r.unit || ""}
              onChange={(e) => update(r.uid, "unit", e.target.value)} />
          </div>
          <div>
            <label className="field-label">City</label>
            <input className="field-input" value={r.city || ""}
              onChange={(e) => update(r.uid, "city", e.target.value)} />
          </div>
          <div>
            <label className="field-label">State / Province</label>
            <input className="field-input" value={r.state || ""}
              onChange={(e) => update(r.uid, "state", e.target.value)} />
          </div>
          <div>
            <label className="field-label">Postal / ZIP</label>
            <input className="field-input" value={r.postal_code || ""}
              onChange={(e) => update(r.uid, "postal_code", e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="field-label">Country</label>
            <input className="field-input" value={r.country || ""}
              onChange={(e) => update(r.uid, "country", e.target.value)} />
          </div>
          <div>
            <label className="field-label">From</label>
            <DateField value={r.from} onCommit={(iso) => update(r.uid, "from", iso)}
              testId={`addr-from-${r.uid}`} />
          </div>
          <div>
            <label className="field-label">To (or &ldquo;Present&rdquo;)</label>
            <DateField value={r.to} onCommit={(iso) => update(r.uid, "to", iso)}
              testId={`addr-to-${r.uid}`} />
          </div>
        </div>
      )}
    </RepeaterCard>
  );
}

/* ---------- Employment History (last 5 years) ---------- */

function EmploymentHistorySection({ caseData, onSave }) {
  const { list, ensureIds } = useRepeater(caseData, "employment_history", {});
  const key = "employment_history";

  const add = () =>
    onSave(key, [
      ...list,
      { uid: crypto.randomUUID(), employer: "", occupation: "", address: "", city: "", country: "", from: "", to: "" },
    ]);
  const update = (uid, k, v) =>
    onSave(key, list.map((r) => (r.uid === uid ? { ...r, [k]: v } : r)));
  const remove = (uid) => onSave(key, ensureIds(list).filter((r) => r.uid !== uid));

  return (
    <RepeaterCard
      title="Employer"
      empty="No employers added yet. Include employment or periods of unemployment for the last 5 years."
      list={list}
      onAdd={add}
      onRemove={remove}
      testId="employment-history"
      addLabel="Add employer"
    >
      {(r) => (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="field-label">Employer name</label>
            <input className="field-input" value={r.employer || ""}
              onChange={(e) => update(r.uid, "employer", e.target.value)}
              data-testid={`emp-name-${r.uid}`} />
          </div>
          <div>
            <label className="field-label">Occupation</label>
            <input className="field-input" value={r.occupation || ""}
              onChange={(e) => update(r.uid, "occupation", e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="field-label">Address</label>
            <input className="field-input" value={r.address || ""}
              onChange={(e) => update(r.uid, "address", e.target.value)} />
          </div>
          <div>
            <label className="field-label">City</label>
            <input className="field-input" value={r.city || ""}
              onChange={(e) => update(r.uid, "city", e.target.value)} />
          </div>
          <div>
            <label className="field-label">Country</label>
            <input className="field-input" value={r.country || ""}
              onChange={(e) => update(r.uid, "country", e.target.value)} />
          </div>
          <div>
            <label className="field-label">From</label>
            <DateField value={r.from} onCommit={(iso) => update(r.uid, "from", iso)}
              testId={`emp-from-${r.uid}`} />
          </div>
          <div>
            <label className="field-label">To (or &ldquo;Present&rdquo;)</label>
            <DateField value={r.to} onCommit={(iso) => update(r.uid, "to", iso)}
              testId={`emp-to-${r.uid}`} />
          </div>
        </div>
      )}
    </RepeaterCard>
  );
}

/* ---------- Parents (Petitioner + Beneficiary) ---------- */

function ParentCard({ title, personKey, testId, data, onUpdate }) {
  const p = data[personKey] || {};
  return (
    <div className="card-surface p-6 mb-4" data-testid={testId}>
      <div className="font-display font-bold mb-3 text-sm" style={{ color: "var(--text-primary)" }}>
        {title}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="field-label">First name</label>
          <input className="field-input" value={p.first_name || ""}
            onChange={(e) => onUpdate(personKey, "first_name", e.target.value)}
            data-testid={`${testId}-first`} />
        </div>
        <div>
          <label className="field-label">Middle name</label>
          <input className="field-input" value={p.middle_name || ""}
            onChange={(e) => onUpdate(personKey, "middle_name", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Last name</label>
          <input className="field-input" value={p.last_name || ""}
            onChange={(e) => onUpdate(personKey, "last_name", e.target.value)}
            data-testid={`${testId}-last`} />
        </div>
        <div>
          <label className="field-label">Date of birth</label>
          <DateField value={p.dob} onCommit={(iso) => onUpdate(personKey, "dob", iso)}
            testId={`${testId}-dob`} />
        </div>
        <div>
          <label className="field-label">City / town of birth</label>
          <input className="field-input" value={p.city_of_birth || ""}
            onChange={(e) => onUpdate(personKey, "city_of_birth", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Country of birth</label>
          <input className="field-input" value={p.country_of_birth || ""}
            onChange={(e) => onUpdate(personKey, "country_of_birth", e.target.value)} />
        </div>
        <div>
          <label className="field-label">City / town of residence</label>
          <input className="field-input" value={p.city_of_residence || ""}
            onChange={(e) => onUpdate(personKey, "city_of_residence", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Country of residence</label>
          <input className="field-input" value={p.country_of_residence || ""}
            onChange={(e) => onUpdate(personKey, "country_of_residence", e.target.value)} />
        </div>
      </div>
    </div>
  );
}

function FamilyHistorySection({ caseData, onSave }) {
  const d = caseData.data || {};

  const update = (personKey, field, value) => {
    const current = d[personKey] || {};
    onSave(personKey, { ...current, [field]: value });
  };

  return (
    <div>
      <div className="mb-2 text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
        Petitioner&rsquo;s parents
      </div>
      <ParentCard title="Petitioner — Father" personKey="petitioner_father"  testId="petitioner-father" data={d} onUpdate={update} />
      <ParentCard title="Petitioner — Mother" personKey="petitioner_mother"  testId="petitioner-mother" data={d} onUpdate={update} />
      <div className="mt-6 mb-2 text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
        Beneficiary&rsquo;s parents
      </div>
      <ParentCard title="Beneficiary — Father" personKey="beneficiary_father" testId="beneficiary-father" data={d} onUpdate={update} />
      <ParentCard title="Beneficiary — Mother" personKey="beneficiary_mother" testId="beneficiary-mother" data={d} onUpdate={update} />
    </div>
  );
}

/* ---------- Pre-flight Scorecard ---------- */

function _pctColor(pct) {
  if (pct >= 100) return "#065F46";   // green
  if (pct >= 70)  return "#B45309";   // amber
  return "#B91C1C";                    // red
}

function ScorecardStat({ label, value, tone, testId }) {
  const bg = {
    green:  "#ECFDF5",
    amber:  "#FEF3C7",
    red:    "#FEF2F2",
    purple: "#F5F3FF",
    neutral: "#F9FAFB",
  }[tone] || "#F9FAFB";
  const color = {
    green:  "#065F46",
    amber:  "#B45309",
    red:    "#B91C1C",
    purple: "#6D28D9",
    neutral: "#374151",
  }[tone] || "#374151";
  return (
    <div
      className="rounded-lg px-4 py-3 flex-1 min-w-[120px]"
      style={{ background: bg }}
      data-testid={testId}
    >
      <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color }}>{label}</div>
      <div className="font-display font-extrabold text-2xl" style={{ color }}>{value}</div>
    </div>
  );
}

function PreflightScorecard({ caseData, documents, flags }) {
  const requiredDocs = caseData.required_documents || [];
  const missingFields = caseData.missing_fields || [];
  const pct = Math.round(caseData.completion_percent ?? 0);

  const uploadedDocTypes = new Set(
    documents.filter((doc) => doc.confirmed).map((doc) => doc.doc_type),
  );
  const missingDocs = requiredDocs.filter((rd) => !uploadedDocTypes.has(rd.id));

  const flagCounts = flags.reduce(
    (acc, f) => {
      if (f.status === "red")    acc.red    += 1;
      if (f.status === "yellow") acc.yellow += 1;
      if (f.status === "purple") acc.purple += 1;
      return acc;
    },
    { red: 0, yellow: 0, purple: 0 },
  );

  const isReady =
    pct >= 100 &&
    missingDocs.length === 0 &&
    flagCounts.red === 0 &&
    flagCounts.yellow === 0;

  const barColor = _pctColor(pct);

  return (
    <div
      className="mb-6 p-6 rounded-xl anim-in"
      data-testid="preflight-scorecard"
      style={{ background: "var(--surface-cream)", border: "1px solid var(--border-stone)" }}
    >
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="font-display font-extrabold text-lg" style={{ color: "var(--text-primary)" }}>
            Pre-flight checklist
          </div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Live snapshot of everything that stands between your family and the consulate interview.
          </div>
        </div>
        {isReady ? (
          <div
            data-testid="preflight-ready-pill"
            className="px-4 py-2 rounded-full font-display font-bold text-sm flex items-center gap-2"
            style={{ background: "#065F46", color: "#ECFDF5" }}
          >
            <span style={{ fontSize: "1.1em" }}>&#10003;</span> Ready for ceac.state.gov
          </div>
        ) : (
          <div
            data-testid="preflight-not-ready-pill"
            className="px-4 py-2 rounded-full font-display font-bold text-sm"
            style={{ background: "#FEF3C7", color: "#92400E" }}
          >
            Not ready — see items below
          </div>
        )}
      </div>

      {/* Field completion bar */}
      <div className="mb-5">
        <div className="flex justify-between text-xs mb-1">
          <span style={{ color: "var(--text-secondary)" }}>DS-260 field completion</span>
          <span data-testid="preflight-completion-pct" style={{ color: barColor, fontWeight: 700 }}>
            {pct}%
          </span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--border-stone)" }}>
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
        </div>
        <div className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
          {missingFields.length === 0
            ? "All required fields collected."
            : `${missingFields.length} required field${missingFields.length === 1 ? "" : "s"} still empty.`}
        </div>
      </div>

      {/* Stat tiles */}
      <div className="flex gap-3 flex-wrap mb-5">
        <ScorecardStat
          label="Red flags"
          value={flagCounts.red}
          tone={flagCounts.red > 0 ? "red" : "green"}
          testId="preflight-red-count"
        />
        <ScorecardStat
          label="Yellow flags"
          value={flagCounts.yellow}
          tone={flagCounts.yellow > 0 ? "amber" : "green"}
          testId="preflight-yellow-count"
        />
        <ScorecardStat
          label="Purple flags"
          value={flagCounts.purple}
          tone={flagCounts.purple > 0 ? "purple" : "neutral"}
          testId="preflight-purple-count"
        />
        <ScorecardStat
          label="Missing docs"
          value={missingDocs.length}
          tone={missingDocs.length > 0 ? "red" : "green"}
          testId="preflight-missing-docs-count"
        />
      </div>

      {/* Missing docs detail */}
      {missingDocs.length > 0 && (
        <div
          className="rounded-lg p-4 text-xs"
          data-testid="preflight-missing-docs-list"
          style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#7F1D1D" }}
        >
          <div className="font-semibold mb-1">Supporting documents still needed:</div>
          <ul className="list-disc list-inside space-y-0.5">
            {missingDocs.map((rd) => (
              <li key={rd.id}>{rd.label}</li>
            ))}
          </ul>
        </div>
      )}

      {isReady && (
        <div
          className="rounded-lg p-4 text-sm mt-1"
          style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", color: "#065F46" }}
        >
          Everything checks out. Download your DS-260 worksheets below and enter the answers at ceac.state.gov.
        </div>
      )}
    </div>
  );
}

/* ---------- Review ---------- */

function WorkflowBanner({ caseData, documents }) {
  const d = caseData.data || {};
  // 4-step consular workflow progress
  const steps = [
    {
      id: "docs",
      label: "Documents uploaded",
      done: (documents || []).length > 0,
      hint: "Use Smart Sort on the People & Documents stage to auto-fill the DS-260s.",
    },
    {
      id: "fields",
      label: "All required answers complete",
      done: (caseData.missing_fields || []).length === 0,
      hint: "Work through each section — we pre-fill everything we can from your documents.",
    },
    {
      id: "fee",
      label: "Preparation fee settled",
      done: ["paid", "waived"].includes(caseData.payment_status),
      hint: "Settle the preparation fee to finish your DS-260 worksheets.",
    },
    {
      id: "ceac",
      label: "DS-260 submitted at ceac.state.gov",
      done: !!d.ds260_confirmation_number,
      hint: "Enter your answers at ceac.state.gov, then record your confirmation number in the DS-260 section.",
    },
  ];
  const nextStep = steps.find((s) => !s.done);

  return (
    <div
      className="mb-5 p-5 rounded-lg anim-in"
      data-testid="workflow-banner"
      style={{ background: "var(--surface-cream)", border: "1px solid var(--border-stone)" }}
    >
      <div className="flex items-center justify-between mb-3 gap-4">
        <div>
          <div
            className="font-display font-bold"
            style={{ color: "var(--text-primary)" }}
          >
            Your consular workflow
          </div>
          <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
            {nextStep
              ? <>Next: <strong>{nextStep.label}</strong> — {nextStep.hint}</>
              : <>All 4 steps complete. Bring your CEAC confirmation page to the consulate interview.</>}
          </div>
        </div>
      </div>
      <ol className="grid grid-cols-4 gap-2">
        {steps.map((step, i) => (
          <li
            key={step.id}
            className="flex items-start gap-2 text-xs"
            data-testid={`workflow-step-${step.id}`}
          >
            <span
              className="w-5 h-5 rounded-full text-[10px] flex items-center justify-center shrink-0 font-bold"
              style={{
                background: step.done ? "#ECFDF5" : "var(--ai-bg)",
                color: step.done ? "#065F46" : "var(--ai-text)",
                border: `1px solid ${step.done ? "#A7F3D0" : "var(--ai-border)"}`,
              }}
            >
              {step.done ? "✓" : i + 1}
            </span>
            <span
              className={step.done ? "" : "font-medium"}
              style={{
                color: step.done ? "var(--text-secondary)" : "var(--text-primary)",
                textDecoration: step.done ? "line-through" : "none",
              }}
            >
              {step.label}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ReviewSection({ caseData, documents = [] }) {
  const d = caseData.data || {};
  const [exporting, setExporting] = useState(false);
  const [flags, setFlags] = useState([]);

  useEffect(() => {
    api.get(`/cases/${caseData.id}/flags`)
      .then((r) => setFlags(r.data.flags))
      .catch(() => {});
  }, [caseData.id]);

  const resolve = useCallback(async (field, action) => {
    await api.post(`/cases/${caseData.id}/fields/${field}/resolve`, { action });
    const { data } = await api.get(`/cases/${caseData.id}/flags`);
    setFlags(data.flags);
  }, [caseData.id]);

  const downloadDS260 = useCallback(async (bundle) => {
    try {
      const path = bundle ? `/cases/${caseData.id}/ds260-bundle.pdf` : `/cases/${caseData.id}/ds260.pdf`;
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = bundle ? "ds260-family-bundle.pdf" : "ds260-worksheet.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("DS-260 download failed:", err?.message || err);
      alert("Could not download the DS-260 worksheet yet. Complete the required fields first, then try again.");
    }
  }, [caseData.id]);

  const downloadExport = useCallback(async () => {
    setExporting(true);
    try {
      const { data } = await api.get(`/cases/${caseData.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const beneficiary =
        [d.beneficiary_first_name, d.beneficiary_last_name].filter(Boolean).join("-") || "case";
      a.href = url;
      a.download = `ds260usa-${beneficiary}-${caseData.id.slice(0, 8)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Case export failed:", err?.message || err);
    } finally {
      setExporting(false);
    }
  }, [caseData.id, d.beneficiary_first_name, d.beneficiary_last_name]);

  const section = (title, items) => (
    <div className="card-surface p-6 mb-4" key={title}>
      <div className="font-display font-bold mb-3" style={{ color: "var(--text-primary)" }}>
        {title}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {items.map(([k, v]) => (
          <div key={k} className="contents">
            <dt style={{ color: "var(--text-secondary)" }}>{k}</dt>
            <dd style={{ color: "var(--text-primary)" }}>
              {v || (
                <span className="italic" style={{ color: "#B45309" }}>
                  Missing
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );

  const missing = caseData.missing_fields?.length || 0;

  return (
    <div>
      <WorkflowBanner
        caseData={caseData}
        documents={documents}
      />

      <PreflightScorecard caseData={caseData} documents={documents} flags={flags} />

      <TimelineCard caseData={caseData} />

      <NoticesCard caseData={caseData} />

      <ShareWithAttorneyCard caseData={caseData} />

      <div className="mb-5"><AssignAttorneyCard caseData={caseData} /></div>

      <div className="mb-5"><BeneficiaryInvitesCard caseId={caseData.id} caseData={caseData} /></div>

      <PaymentBanner caseData={caseData} />

      <div
        className="mb-5 p-5 rounded-lg flex items-center justify-between gap-4 anim-in"
        data-testid="export-card"
        style={{
          background: "var(--ai-bg)",
          border: "1px solid var(--ai-border)",
        }}
      >
        <div>
          <div
            className="font-display font-bold text-sm mb-1"
            style={{ color: "var(--ai-text)" }}
          >
            Save a copy of your case
          </div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Download a complete JSON snapshot — every field, uploaded document, and chat
            message. Email it to yourself, keep it on your phone, or share it with a lawyer.
          </div>
        </div>
        <button
          data-testid="export-case-btn"
          onClick={downloadExport}
          disabled={exporting}
          className="btn-primary flex items-center gap-1.5 shrink-0"
        >
          <Download className="w-4 h-4" />
          {exporting ? "Preparing…" : "Download snapshot"}
        </button>
      </div>

      {/* DS-260 worksheet downloads */}
      <div
        className="mb-5 p-5 rounded-lg anim-in"
        data-testid="ds260-worksheets-card"
        style={{
          background: "var(--surface-cream)",
          border: "1px solid var(--border-stone)",
        }}
      >
        <div className="font-display font-bold text-sm mb-1" style={{ color: "var(--text-primary)" }}>
          DS-260 worksheets
        </div>
        <div className="text-xs mb-4" style={{ color: "var(--text-secondary)" }}>
          Download a printable worksheet with every answer organized in the same
          order as the online DS-260 at ceac.state.gov — one per family member.
          Review each value, then enter your answers on the official site.
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            data-testid="download-ds260-btn"
            onClick={() => downloadDS260(false)}
            className="btn-primary flex items-center gap-1.5"
          >
            <Download className="w-4 h-4" />
            Download DS-260 worksheet
          </button>
          <button
            data-testid="download-ds260-bundle-btn"
            onClick={() => downloadDS260(true)}
            className="btn-ghost flex items-center gap-1.5 border rounded-lg px-3 py-2"
            style={{ borderColor: "var(--border-stone)" }}
          >
            <Download className="w-4 h-4" />
            Family bundle (all applicants)
          </button>
          <a
            href="https://ceac.state.gov/IV/Login.aspx"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="open-ceac-link"
            className="btn-ghost flex items-center gap-1.5 border rounded-lg px-3 py-2"
            style={{ borderColor: "var(--border-stone)" }}
          >
            <ExternalLink className="w-4 h-4" />
            Open ceac.state.gov
          </a>
        </div>
        <div
          className="mt-4 p-3 rounded-md text-xs font-medium"
          data-testid="nvc-fee-compliance-note"
          style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#7F1D1D" }}
        >
          Government fees (AOS fee and the IV fee for each applicant) are paid ONLY
          online at ceac.state.gov. Never mail a payment to NVC, and never pay a fee
          for a family member NVC hasn't invoiced.
        </div>
      </div>

      {missing > 0 && (
        <div
          className="mb-4 p-4 rounded-md flex gap-2 items-start"
          style={{
            background: "var(--warn-bg)",
            border: "1px solid var(--warn-border)",
            color: "var(--warn-text)",
          }}
          data-testid="review-warning"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div className="text-sm">
            <b>{missing} required field(s)</b> still need attention before you can finalize
            the case.
          </div>
        </div>
      )}
      {section("Petitioner (Sponsor)", [
        ["Name", `${d.petitioner_first_name || ""} ${d.petitioner_last_name || ""}`.trim()],
        ["Date of birth", d.petitioner_dob],
        ["Place of birth", d.petitioner_place_of_birth],
        ["U.S. status", d.petitioner_citizenship_status],
        ["A-Number / SSN", d.petitioner_a_number_or_ssn],
        ["Address", d.petitioner_address],
        ["Phone", d.petitioner_phone],
        ["Email", d.petitioner_email],
      ])}
      {section("Beneficiary (Applicant)", [
        ["Name", `${d.beneficiary_first_name || ""} ${d.beneficiary_last_name || ""}`.trim()],
        ["Date of birth", d.beneficiary_dob],
        ["Place of birth", d.beneficiary_place_of_birth],
        ["Nationality", d.beneficiary_nationality],
        ["Marital status", d.beneficiary_marital_status],
        ["Address", d.beneficiary_address],
      ])}
      {caseData.relationship === "spouse" &&
        section("Marriage", [
          ["Marriage date", d.marriage_date],
          ["Marriage place", d.marriage_place],
          ["Previously married", d.spouse_previously_married],
          ["Prior marriage end date", d.prior_marriage_end_date],
        ])}
    </div>
  );
}

/* -------- PaymentBanner (Review stage) -------- */
function PaymentBanner({ caseData }) {
  const waived = caseData?.fee_waived === true || caseData?.payment_status === "waived";
  const paid = caseData?.payment_status === "paid" || waived;
  const accepted = caseData?.status === "CASE_ACCEPTED";
  const [accepting, setAccepting] = useState(false);
  const [acceptErr, setAcceptErr] = useState("");
  const nav = useNavigate();

  const acceptCase = async () => {
    if (!window.confirm(
      "Are you sure? By accepting, you confirm your case package is complete and " +
      "ready to print and mail to USCIS. No further edits or refunds are possible " +
      "after acceptance."
    )) return;
    setAccepting(true);
    setAcceptErr("");
    try {
      await api.post(`/cases/${caseData.id}/accept`, { confirm: true });
      // Refresh the page so the accepted state is reflected everywhere.
      window.location.reload();
    } catch (e) {
      setAcceptErr(e.response?.data?.detail || e.message);
      setAccepting(false);
    }
  };
  if (waived) {
    return (
      <div
        className="mb-5 p-5 rounded-lg flex items-center justify-between gap-4 anim-in"
        data-testid="payment-banner-waived"
        style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}
      >
        <div>
          <div className="font-display font-bold text-sm mb-1" style={{ color: "var(--ai-text)" }}>
            Fee waived — thanks for being a beta tester! 🎁
          </div>
          <div className="text-xs" style={{ color: "var(--ai-text)" }}>
            An administrator has comped this case. You have full access to the filing packet at no charge.
          </div>
        </div>
      </div>
    );
  }
  if (paid) {
    if (accepted) {
      return (
        <div className="mb-5 p-5 rounded-lg anim-in" data-testid="payment-banner-accepted"
             style={{ background: "#ECFDF5", border: "1px solid #A7F3D0" }}>
          <div className="font-display font-bold text-sm mb-1" style={{ color: "#065F46" }}>
            Case accepted as complete ✓
          </div>
          <div className="text-xs" style={{ color: "#065F46" }}>
            You confirmed this case is ready to print and mail on{" "}
            {caseData?.case_accepted_at?.slice(0, 10)}. No further edits are possible.
          </div>
        </div>
      );
    }
    return (
      <div className="mb-5 p-5 rounded-lg anim-in flex flex-col md:flex-row md:items-center gap-4"
           data-testid="payment-banner-paid"
           style={{ background: "#ECFDF5", border: "1px solid #A7F3D0" }}>
        <div className="flex-1">
          <div className="font-display font-bold text-sm mb-1" style={{ color: "#065F46" }}>
            Case preparation fee paid ✓
          </div>
          <div className="text-xs" style={{ color: "#065F46" }}>
            Please review your final packet. When everything looks good, click below to
            accept your case as complete and ready for filing. This finalizes the case —
            no further edits or refunds after acceptance.
          </div>
          {acceptErr && (
            <div className="mt-2 text-xs p-2 rounded" style={{ background: "#FEF2F2", color: "#991B1B" }}>
              {acceptErr}
            </div>
          )}
        </div>
        <button className="btn-primary shrink-0 text-sm inline-flex items-center gap-1.5"
                onClick={acceptCase} disabled={accepting}
                data-testid="accept-case-btn">
          {accepting ? "Accepting…" : "Accept Case as Complete and Ready for Filing"}
        </button>
      </div>
    );
  }
  return (
    <div
      className="mb-5 p-5 rounded-lg flex items-center justify-between gap-4 anim-in"
      data-testid="payment-banner"
      style={{
        background: "linear-gradient(135deg, #FDF8F0 0%, #FFFFFF 60%)",
        border: "1px solid var(--ai-border)",
      }}
    >
      <div>
        <div className="font-display font-bold text-sm mb-1" style={{ color: "var(--action)" }}>
          Ready to finalize? Pay the case preparation fee.
        </div>
        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
          One-time fee — includes every family member's DS-260 preparation. Government
          fees are paid separately — and only — at ceac.state.gov.
        </div>
      </div>
      <a
        href={`/case/${caseData.id}/pay`}
        data-testid="pay-preparation-btn"
        className="btn-primary flex items-center gap-1.5 shrink-0"
      >
        View pricing &amp; pay
      </a>
    </div>
  );
}

